// Direct imports on this page are needed for the algolia-update script to run.  Otherwise MODULE NOT FOUND error will be thrown.
import kebabCase from 'lodash/kebabCase'
import qs from 'qs'
import { WatchableNode } from '@/services/ProjectsService'
import { ContentEpisode, ContentEpisodeEdge } from '@/types/codegen-federation'
import locales from '../../constants/locales'
import { projectTypes } from '../../constants/projectTypes'
import { slugs } from '../../constants/slugs'
import { Episode, Project, ProjectType } from '../../services/ProjectsService'
import { Media } from '../../utils/EpisodeUtil/Media'
import { isDefined } from '../primitives/generics'
import { TranslateFunction } from '../translate/translate-client'
import { Maybe } from '../types'

interface ProjectEpisodeTitleComponents {
  projectName: string
  projectType?: string
  seasonNumber: number
  episodeNumber: number
}

export const buildProjectEpisodeTitle = ({
  projectName,
  projectType,
  seasonNumber,
  episodeNumber,
}: ProjectEpisodeTitleComponents): string => {
  if (projectType === 'series') {
    return `S${seasonNumber}:E${episodeNumber} ${projectName}`
  } else {
    return projectName
  }
}

interface EpisodeTitleComponents {
  title: string
  projectType?: ProjectType
  seasonNumber: number
  episodeNumber: number
}

export const buildEpisodeTitle = ({
  title,
  projectType,
  seasonNumber,
  episodeNumber,
}: EpisodeTitleComponents): string => {
  if (projectType === 'series') {
    return `S${seasonNumber} E${episodeNumber} - ${title}`
  } else {
    return title
  }
}

interface AbbreviatedEpisodeReferenceComponents {
  name: string
  seasonNumber: number
  episodeNumber: number
  projectType?: ProjectType
}

export const buildAbbreviatedEpisodeReference = ({
  name,
  episodeNumber,
  seasonNumber,
  projectType,
}: AbbreviatedEpisodeReferenceComponents): string => {
  if (projectType === 'series') {
    return `S${seasonNumber} E${episodeNumber}`
  } else {
    return name
  }
}

interface EpisodeDecoratorComponents {
  seasonNumber?: Maybe<number>
  episodeNumber?: Maybe<number>
  subtitle?: Maybe<string>
}

interface EpisodeLinkComponents extends EpisodeDecoratorComponents {
  projectSlug: string
  guid: string
}

/**
 * position and smpte are exclusive only one can be used.
 * This function prefers position over smpte if both are provided.
 */
interface EpisodeLinkOptions {
  // alternative to project slug
  slug?: string
  // seek head position in seconds
  position?: number
  // seek head position in smpte
  smpte?: string
  disableDecoration?: boolean
}

function isEpisodeLinkOptions(options: unknown): options is EpisodeLinkOptions {
  return typeof options === 'object' && Boolean(options)
}

export function buildDecorativeEpisodePath(
  { seasonNumber, episodeNumber, subtitle }: EpisodeDecoratorComponents,
  options?: string | EpisodeLinkOptions,
) {
  const disableDecoration = typeof options !== 'string' ? options?.disableDecoration : false
  if (disableDecoration) return ''

  const seasonNumberParam = typeof seasonNumber === 'number' && seasonNumber >= 0 ? `/season-${seasonNumber}` : ''
  const episodeNumberParam = typeof episodeNumber === 'number' && episodeNumber >= 0 ? `/episode-${episodeNumber}` : ''
  const subTitleParam = subtitle ? `/${kebabCase(subtitle)}` : ''

  const decorativeEpisodePath =
    seasonNumberParam && episodeNumberParam && subTitleParam
      ? `${seasonNumberParam}${episodeNumberParam}${subTitleParam}`
      : ''
  return decorativeEpisodePath
}

export function buildLinkToWatchable(
  projectType: ProjectType,
  episode: EpisodeLinkComponents,
  locale: string,
  options?: EpisodeLinkOptions,
): string {
  return projectType === projectTypes.series && episode.projectSlug !== slugs.dryBar
    ? buildLinkToEpisode(
        {
          projectSlug: episode.projectSlug,
          episodeNumber: episode.episodeNumber,
          subtitle: episode.subtitle,
          seasonNumber: episode.seasonNumber,
          guid: episode.guid,
        },
        { ...options, disableDecoration: locale !== locales.defaultLocale },
      )
    : `/watch/${episode.projectSlug}/episode/${episode.guid}${qs.stringify(
        {
          position: options?.position ? Math.trunc(options.position) : null,
          smpte: !options?.position && options?.smpte ? options.smpte : null,
        },
        {
          skipNulls: true,
          addQueryPrefix: true,
        },
      )}`
}

export function buildLinkToEpisode(
  { projectSlug, guid, seasonNumber, episodeNumber, subtitle }: EpisodeLinkComponents,
  options?: string | EpisodeLinkOptions,
): string {
  const decorativeEpisodePath = buildDecorativeEpisodePath({ seasonNumber, episodeNumber, subtitle }, options)

  let projectSlugParam = projectSlug
  let position
  let smpte

  if (typeof options === 'string') {
    projectSlugParam = projectSlugParam ?? options
  } else if (isEpisodeLinkOptions(options)) {
    projectSlugParam = projectSlugParam ?? options.slug
    position = options.position ? Math.trunc(options.position) : null
    smpte = !position && options.smpte ? options.smpte : null
  }

  return `/watch/${projectSlugParam}/episode/${guid}${decorativeEpisodePath}${qs.stringify(
    { position, smpte },
    {
      skipNulls: true,
      addQueryPrefix: true,
    },
  )}`
}

export function episodesToMedias(episodes: Episode[] = [], options: EpisodeToMediaOptions = {}): Media[] {
  return episodes.map((episode) => {
    return episodeToMedia(episode, options)
  })
}

interface EpisodeToMediaOptions {
  displayDescription?: boolean
  hasEarlyAccess?: boolean
  projectName?: string
  projectType?: ProjectType
  trailerText?: string
  locale?: string
  position?: number
  progress?: number
}

const defaultEpisodeToMediaOptions: EpisodeToMediaOptions = {
  displayDescription: true,
  hasEarlyAccess: false,
  locale: 'en',
  projectType: 'series',
}

export function episodeToMedia(episode: Episode, options: EpisodeToMediaOptions = defaultEpisodeToMediaOptions): Media {
  const locale = options.locale ?? locales.defaultLocale
  const episodeTitle =
    episode.isTrailer && episode?.name?.toLowerCase().includes('trailer') ? options?.trailerText : episode?.name
  const episodeWatchPosition = episode?.watchPosition?.position
  const episodeDuration = episode?.source?.duration
  const progress = episodeWatchPosition && episodeDuration ? (episodeWatchPosition / episodeDuration) * 100 : 0

  return {
    description: episode.description,
    earlyAccessDate: episode.earlyAccessDate,
    episodeNumber: episode.episodeNumber,
    episodeSlug: episode.slug,
    guid: episode.guid,
    href: buildLinkToWatchable(options?.projectType ?? 'series', episode, locale, { position: options.position }),
    poster: episode.posterLandscapeCloudinaryPath ?? episode.posterCloudinaryPath,
    projectSlug: episode.projectSlug,
    publiclyAvailableDate: episode.publiclyAvailableDate,
    seasonNumber: episode.seasonNumber,
    subtitle: episode.subtitle,
    title: episodeTitle,
    unavailableReason: episode.unavailableReason,
    progress,
    ...options,
  }
}

export function isAPreview(episode: Maybe<Pick<Episode, 'name'>>): boolean {
  return Boolean(episode?.name?.toLowerCase().includes('preview'))
}

export function isASneakPeek(episode: Maybe<Pick<Episode, 'name' | 'slug'>>): boolean {
  return Boolean(
    episode?.name?.toLowerCase()?.includes('sneak peek') ||
      episode?.name?.toLowerCase()?.includes('sneek peak') ||
      episode?.slug?.includes('sneak-peek') ||
      episode?.slug?.includes('the-first-'),
  )
}

export function isATorch(episode: Maybe<Pick<Episode, 'subtitle'>>): boolean {
  return Boolean(episode?.subtitle?.toLowerCase()?.includes('torch'))
}

// Convert movie/special/sneak peek/bonus watchables into Media cards
type SupportedWatchable = Extract<
  WatchableNode,
  | { __typename: 'ContentMovie' }
  | { __typename: 'ContentSpecial' }
  | { __typename: 'ContentSneakPeek' }
  | { __typename: 'ContentBonus' }
  | { __typename: 'ContentTrailer' }
>

function isSupportedWatchable(node: WatchableNode | null | undefined): node is SupportedWatchable {
  return (
    !!node &&
    (node.__typename === 'ContentMovie' ||
      node.__typename === 'ContentSpecial' ||
      node.__typename === 'ContentSneakPeek' ||
      node.__typename === 'ContentBonus' ||
      node.__typename === 'ContentTrailer')
  )
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export function watchablesToMedias(watchables: WatchableNode[] = [], options: EpisodeToMediaOptions = {}): Media[] {
  const nodes = (watchables ?? []).filter(isSupportedWatchable) as SupportedWatchable[]

  // eslint-disable-next-line sonarjs/cognitive-complexity
  return nodes.map((node) => {
    const duration: number | undefined = node.duration ?? undefined
    const position: number | undefined = node.watchPosition?.position ?? undefined
    const poster: string = node.posterLandscapeCloudinaryPath ?? ''
    const projectSlug: string = node.project?.slug ?? ''
    const progress = duration && position ? (position / duration) * 100 : 0
    const hrefBase =
      node.__typename === 'ContentBonus' || node.__typename === 'ContentTrailer'
        ? `/watch/${projectSlug}/videos/bonus/${node.id}`
        : `/watch/${projectSlug}/episode/${node.id}`
    const href = position
      ? `${hrefBase}${qs.stringify({ position: Math.trunc(position) }, { addQueryPrefix: true })}`
      : hrefBase

    const normalizedTitle = getNormalizedTitle(node)

    const description: string | undefined = 'description' in node ? node.description ?? undefined : undefined

    const toWatchableType = (): import('@/types/codegen-federation').WatchableType | undefined => {
      switch (node.__typename) {
        case 'ContentTrailer':
          return 'TRAILER'
        case 'ContentBonus':
          return 'BONUS_CONTENT'
        case 'ContentSpecial':
          return 'SPECIAL'
        case 'ContentSneakPeek':
          return 'SNEAK_PEEK'
        case 'ContentMovie':
          return 'MOVIE'
        default:
          return undefined
      }
    }

    return {
      description,
      earlyAccessDate: deriveEarlyAccessDate(node as EpisodeAvailabilityLite | undefined, node.watchableAt),
      episodeNumber: undefined,
      episodeSlug: node.slug,
      guid: node.id,
      href,
      poster,
      projectSlug,
      subtitle: node.subtitle,
      title: normalizedTitle,
      progress,
      duration,
      position,
      projectType: (options.projectType as ProjectType) ?? 'movie',
      watchableAvailabilityStatus: node.watchableAvailabilityStatus ?? null,
      hasEarlyAccess: options.hasEarlyAccess,
      watchableType: toWatchableType(),
    }
  })
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export function watchablesEpisodesToMedias(
  watchables: WatchableNode[] = [],
  options: EpisodeToMediaOptions = {},
): Media[] {
  const nodes = (watchables ?? []).filter((node) => !!node && node.__typename === 'ContentEpisode') as ContentEpisode[]

  return nodes.map((node) => {
    const duration: number | undefined = node.duration ?? undefined
    const position: number | undefined = node.watchPosition?.position ?? undefined
    const poster: string = node.posterLandscapeCloudinaryPath ?? ''
    const projectSlug: string = node.project?.slug ?? ''
    const progress = duration && position ? (position / duration) * 100 : 0
    const hrefBase = `/watch/${projectSlug}/episode/${node.id}`
    const href = position
      ? `${hrefBase}${qs.stringify({ position: Math.trunc(position) }, { addQueryPrefix: true })}`
      : hrefBase

    const normalizedTitle = getNormalizedTitle(node)

    const description: string | undefined = 'description' in node ? node.description ?? undefined : undefined

    return {
      description,
      earlyAccessDate: deriveEarlyAccessDate(node as EpisodeAvailabilityLite | undefined, node.watchableAt),
      episodeNumber: node.episodeNumber,
      episodeSlug: node.slug,
      guid: node.id,
      href,
      poster,
      projectSlug,
      subtitle: node.subtitle ?? '',
      title: normalizedTitle,
      progress,
      duration,
      position,
      projectType: (options.projectType as ProjectType) ?? 'movie',
      watchableAvailabilityStatus: node.watchableAvailabilityStatus ?? null,
      hasEarlyAccess: options.hasEarlyAccess,
      watchableType: 'EPISODE',
    }
  })
}

/**
 * Returns the normalized title for a watchable node, handling all fallback logic.
 */
type ContentBonusWithNodeName = {
  __typename: 'ContentBonus'
  nodeName?: string
  name?: string
  subtitle?: string
  slug?: string
}
type ContentSneakPeekWithNodeName = {
  __typename: 'ContentSneakPeek'
  nodeName?: string
  name?: string
  subtitle?: string
  slug?: string
}

function getNodeNameFallback(node: SupportedWatchable | ContentEpisode, rawName: string): string {
  const typename = node.__typename
  if (typename === 'ContentBonus' && (!rawName || rawName.trim() === '')) {
    const bonusNode = node as ContentBonusWithNodeName
    return typeof bonusNode.nodeName === 'string' ? bonusNode.nodeName : ''
  }
  if (typename === 'ContentSneakPeek' && (!rawName || rawName.trim() === '')) {
    const sneakPeekNode = node as ContentSneakPeekWithNodeName
    return typeof sneakPeekNode.nodeName === 'string' ? sneakPeekNode.nodeName : ''
  }
  return rawName
}

const getNormalizedTitle = (node: SupportedWatchable | ContentEpisode): string => {
  const typename = node.__typename
  let rawName = typeof node.name === 'string' ? node.name : ''
  const rawSubtitle = typeof node.subtitle === 'string' ? node.subtitle : ''
  const slug = typeof node.slug === 'string' ? node.slug : ''

  rawName = getNodeNameFallback(node, rawName)

  const isTrailerLike = /trailer/i.test(rawName) || /trailer/i.test(rawSubtitle) || /trailer/i.test(slug)

  let normalizedTitle: string | undefined = rawName
  if (normalizedTitle === '') {
    switch (typename) {
      case 'ContentMovie':
        normalizedTitle = 'MOVIE'
        break
      case 'ContentSneakPeek':
        normalizedTitle = 'SNEAK PEEK'
        break
      case 'ContentSpecial':
        normalizedTitle = isTrailerLike ? 'TRAILER' : 'FEATURETTE'
        break
      case 'ContentBonus':
        normalizedTitle = 'FEATURETTE'
        break
      default:
        normalizedTitle = rawName
    }
  }
  return normalizedTitle ?? ''
}

export const processWatchablesToMediasWithFiltering = (
  watchables: WatchableNode[] | undefined,
  options: {
    projectName?: string
    hasEarlyAccess?: boolean
    projectType?: string
    trailerText: string
    locale: string
  },
  isGeoblockedProject: boolean,
  isGuildMember: boolean,
): Media[] => {
  if (!watchables) return []

  return watchablesToMedias(watchables, {
    hasEarlyAccess: options?.hasEarlyAccess,
    projectType: options?.projectType as ProjectType,
    trailerText: options?.trailerText,
    locale: options?.locale,
  }).filter((media) => {
    // Filter sneak peeks when geoblocked or when user is a Guild member
    const isSneakPeek = isASneakPeek({ slug: media?.episodeSlug ?? '', name: media?.title ?? '' })
    return !(isSneakPeek && (isGeoblockedProject || isGuildMember))
  })
}

export function isATeaser(episode: Maybe<Pick<Episode, 'name' | 'subtitle'>>): boolean {
  return Boolean(
    episode?.name?.toLowerCase()?.includes('teaser') || episode?.subtitle?.toLowerCase()?.includes('teaser'),
  )
}

export function isAPitch(episode: Maybe<Pick<Episode, 'name'>>): boolean {
  return Boolean(episode?.name?.toLowerCase()?.includes('pitch'))
}

export function isARecap(episode: Maybe<Pick<Episode, 'name'>>): boolean {
  return Boolean(episode?.name?.toLowerCase()?.includes('recap'))
}

export function isALivestream(episode: Maybe<Pick<Episode, 'name'>>): boolean {
  return Boolean(episode?.name?.toLowerCase()?.includes('livestream'))
}

export function isAClip(episode: Maybe<Pick<Episode, 'name'>>): boolean {
  return Boolean(episode?.name?.toLowerCase()?.includes('clip'))
}

export function isAFeaturette(episode: Maybe<Pick<Episode, 'name'>>): boolean {
  return Boolean(episode?.name?.toLowerCase().includes('featurette'))
}

interface DefaultCtaProps {
  ctaText: string
  currentEpisode?: Episode
  locale: string
  slug: string
  theaterDescription: string
}

type ShowHeroCta = {
  ctaPath?: string
  ctaText: string
  descriptionTitle?: string
  duration?: number
  loading: boolean
  position?: number
  showDescription: string
}

interface ContinueWatchingProps {
  currentEpisode: Episode
  locale: string
  projectType: ProjectType
  resume: string
  slug: string
}

export function getTrailerCta({
  ctaText,
  currentEpisode,
  locale,
  slug,
  theaterDescription,
}: DefaultCtaProps): ShowHeroCta {
  return {
    loading: false,
    showDescription: theaterDescription,
    ctaPath: currentEpisode
      ? buildLinkToEpisode(currentEpisode as Episode, { slug, disableDecoration: locale !== locales.defaultLocale })
      : undefined,
    ctaText,
  }
}

export function getContinueWatchingCta({
  currentEpisode,
  locale,
  projectType,
  resume,
  slug,
}: ContinueWatchingProps): ShowHeroCta {
  return {
    position: currentEpisode?.watchPosition?.position,
    duration: currentEpisode?.source?.duration,
    loading: false,
    showDescription: currentEpisode?.description,
    descriptionTitle:
      currentEpisode.projectSlug === slugs.dryBar
        ? currentEpisode?.subtitle
        : buildEpisodeTitle({
            title: currentEpisode?.subtitle,
            episodeNumber: currentEpisode?.episodeNumber,
            seasonNumber: currentEpisode?.seasonNumber,
            projectType,
          }),
    ctaPath: currentEpisode
      ? buildLinkToEpisode(currentEpisode as Episode, { slug, disableDecoration: locale !== locales.defaultLocale })
      : undefined,
    ctaText: `${resume} ${buildAbbreviatedEpisodeReference({
      name: currentEpisode?.projectSlug === slugs.dryBar ? currentEpisode?.subtitle : currentEpisode?.name,
      seasonNumber: currentEpisode?.seasonNumber,
      episodeNumber: currentEpisode?.episodeNumber,
    })}`,
  }
}

export function getDefaultCta({
  ctaText,
  currentEpisode,
  locale,
  slug,
  theaterDescription,
}: DefaultCtaProps): ShowHeroCta {
  return {
    loading: false,
    showDescription: theaterDescription,
    ctaPath: currentEpisode
      ? buildLinkToEpisode(currentEpisode as Episode, { slug, disableDecoration: locale !== locales.defaultLocale })
      : undefined,
    ctaText,
  }
}

export function getContinueWatchingCtaNew({
  currentEpisode,
  locale,
  projectType,
  resume,
  slug,
}: ContinueWatchingProps): ShowHeroCta {
  return {
    position: currentEpisode?.watchPosition?.position,
    duration: currentEpisode?.source?.duration,
    loading: false,
    showDescription: currentEpisode?.description,
    descriptionTitle:
      currentEpisode.projectSlug === slugs.dryBar
        ? currentEpisode?.subtitle
        : buildEpisodeTitle({
            title: currentEpisode?.subtitle,
            episodeNumber: currentEpisode?.episodeNumber,
            seasonNumber: currentEpisode?.seasonNumber,
            projectType,
          }),
    ctaPath: currentEpisode
      ? buildLinkToEpisode(currentEpisode as Episode, { slug, disableDecoration: locale !== locales.defaultLocale })
      : undefined,
    ctaText: `${resume} ${buildAbbreviatedEpisodeReference({
      name: currentEpisode?.projectSlug === slugs.dryBar ? currentEpisode?.subtitle : currentEpisode?.name,
      seasonNumber: currentEpisode?.seasonNumber,
      episodeNumber: currentEpisode?.episodeNumber,
      projectType: projectType,
    })}`,
  }
}

// Helper: find episode node for a media GUID in a selected season
function getEpisodeForMedia(
  project: Project | undefined,
  selectedSeasonId: string,
  mediaGuid?: string,
): ContentEpisodeEdge['node'] | undefined {
  const seasons = project?.title?.seasons?.edges ?? []
  const seasonEdge = seasons.find((edge) => edge?.node?.id === selectedSeasonId)
  const episodeEdges = seasonEdge?.node?.episodes?.edges ?? []
  return episodeEdges.find((epEdge: ContentEpisodeEdge) => epEdge?.node?.id === mediaGuid)?.node
}

// Helper: derive early access date for GuildAccessPill from availability fields
type EpisodeAvailabilityLite = {
  watchableAvailabilityStatus?: string | null
  watchableAt?: string | null
  actionsToWatch?: Array<{ __typename?: string; startDatetime?: string | null }> | null
}

function deriveEarlyAccessDate(episode: EpisodeAvailabilityLite | undefined, fallback?: string): string | undefined {
  let derived = fallback
  if (!episode) return derived
  const status = episode.watchableAvailabilityStatus ?? null
  const watchableAt = episode.watchableAt ?? undefined
  const actions = episode.actionsToWatch ?? []

  if (watchableAt) {
    derived = watchableAt
  } else if (status === 'ACTION_REQUIRED' && Array.isArray(actions)) {
    const guildAction = actions.find((a) => a?.__typename === 'GuildAvailabilityAction')
    if (guildAction?.startDatetime) derived = guildAction.startDatetime
  }
  return derived
}

// Helper: when no seasons, optionally attach title-level availability for movie labels
function enhanceMediaForNonSeries(
  project: Project | undefined,
  media: Media,
  options: { projectType?: string },
): Media {
  return {
    ...media,
    ...((options.projectType === 'movie' &&
      (media?.title?.toLowerCase()?.includes('movie') || media?.title?.toLowerCase()?.includes('film')) && {
        watchableAvailabilityStatus: project?.title?.watchableAvailabilityStatus ?? null,
      }) ||
      {}),
  }
}

export const processEpisodesToMediasWithFiltering = (
  episodes: Episode[] | undefined,
  options: {
    projectName?: string
    hasEarlyAccess: boolean
    projectType?: string
    trailerText: string
    locale: string
    selectedSeasonId: string
    isGuildMember?: boolean
  },
  isGeoblockedProject: boolean,
  project?: Project,
): Media[] => {
  if (!episodes) {
    return []
  }

  return episodesToMedias(episodes, {
    projectName: options.projectName,
    hasEarlyAccess: options.hasEarlyAccess,
    projectType: options.projectType as ProjectType,
    trailerText: options.trailerText,
    locale: options.locale,
  })
    .filter(
      (media) => !(isASneakPeek({ slug: media?.episodeSlug ?? '', name: media?.title ?? '' }) && isGeoblockedProject),
    )
    .map((media): Media => {
      // No seasons available (non-series path)
      if (!project?.title?.seasons?.edges) {
        return enhanceMediaForNonSeries(project, media, { projectType: options.projectType })
      }

      const episode = getEpisodeForMedia(project, options.selectedSeasonId, media?.guid)
      const earlyAccessDate = deriveEarlyAccessDate(
        episode as EpisodeAvailabilityLite | undefined,
        media.earlyAccessDate,
      )
      const watchableAvailabilityStatus =
        (episode as ContentEpisodeEdge['node'] | undefined)?.watchableAvailabilityStatus ?? null

      return {
        ...media,
        watchableAvailabilityStatus,
        earlyAccessDate,
      }
    })
}

export const processContentEpisodesToMediasWithFiltering = (
  episodes: ContentEpisodeEdge[] | undefined,
  options: {
    projectName?: string
    projectType?: 'movie' | 'series' | 'podcast' | 'special' | undefined
    trailerText: string
    locale: string
    selectedSeasonId: string
    isGuildMember?: boolean
  },
  isGeoblockedProject: boolean,
  project?: Project,
): Media[] => {
  if (!episodes) {
    return []
  }
  const nodes = episodes.map((edge) => edge.node).filter(isDefined) as WatchableNode[]
  return watchablesEpisodesToMedias(nodes, options)
    .filter((media) => {
      const isSneakPeek = isASneakPeek({ slug: media?.episodeSlug ?? '', name: media?.title ?? '' })
      const isGuild = Boolean(options?.isGuildMember)
      return !(isSneakPeek && (isGeoblockedProject || isGuild))
    })
    .map((media): Media => {
      const episode = getEpisodeForMedia(project, options.selectedSeasonId, media?.guid)
      const earlyAccessDate = deriveEarlyAccessDate(
        episode as EpisodeAvailabilityLite | undefined,
        media.earlyAccessDate,
      )
      const watchableAvailabilityStatus =
        (episode as ContentEpisodeEdge['node'] | undefined)?.watchableAvailabilityStatus ?? null

      return {
        ...media,
        watchableAvailabilityStatus,
        earlyAccessDate,
      }
    })
}

type FormatDurationOptions = {
  includeZeroMinutes?: boolean
}

/**
 * useFormatDuration
 * Formats a duration (in minutes) into a localized, abbreviated string.
 * Examples: "1 hr", "2 hrs", "59 mins", "1 hr 1 min", "2 hrs 3 mins"
 */
export const useFormatDuration = (t: TranslateFunction) => {
  return (totalMinutes?: number | null, options?: FormatDurationOptions): string | undefined => {
    if (totalMinutes == null) return undefined
    if (!Number.isFinite(totalMinutes) || totalMinutes <= 0) return undefined

    const minutesTotal = Math.floor(totalMinutes / 60)

    if (minutesTotal <= 0) {
      const minuteUnit = t('durationMinuteAbbrev', 'min')
      return `1 ${minuteUnit}`
    }

    const hours = Math.floor(minutesTotal / 60)
    const minutes = minutesTotal % 60

    const parts: string[] = []

    if (hours > 0) {
      const hourUnit = hours === 1 ? t('durationHourAbbrev', 'hr') : t('abbreviatedHours', 'hrs')
      parts.push(`${hours} ${hourUnit}`)
    }

    if (minutes > 0 || (hours > 0 && options?.includeZeroMinutes)) {
      const minuteUnit = minutes === 1 ? t('durationMinuteAbbrev', 'min') : t('abbreviatedMinutes', 'mins')
      parts.push(`${minutes} ${minuteUnit}`)
    }

    return parts.join(' ')
  }
}
